import React from "react";
import { useDownloadInvoice } from "../../../hooks/invoice/useDownloadInvoice";
import { DownloadIcon } from "../../../assets/icons/DownloadIcon";
import SendIcon from "../../../assets/icons/SendIcon";
import RecordIcon from "../../../assets/icons/RecordIcon";
import { useUserStore } from "../../../state/useUserStore";
import { Statuses } from "../../../constants";
import { IInvoice } from "../../../@types";
import { ClipIcon } from "../../../assets/icons/Clip";
import { showToast } from "../../../utils/toastUtils";

interface FirstTimerModalProps {
  onClose: () => void;
  invoice: IInvoice;
  invoiceId: string;
  setShowRecordPayment: (show: boolean) => void;
  setShowSendModal: (show: boolean) => void;
  setShowPaymentPrompt: (show: boolean) => void; // Add this prop
}

const FirstTimerModal: React.FC<FirstTimerModalProps> = ({
  onClose,
  invoice,
  invoiceId,
  setShowRecordPayment,
  setShowSendModal,
  setShowPaymentPrompt, // Add this prop
}) => {
  const handleUpdate = (updatedInvoice: IInvoice) => {};

  const { downloadPDF, isLoading } = useDownloadInvoice({
    invoice: invoice,
    invoiceId: invoiceId,
    onUpdate: handleUpdate,
  });

  const handleDownloadInvoice = async () => {
    try {
      await downloadPDF();
      showToast("Invoice downloaded successfully", "success");
      onClose();
    } catch (error) {
      showToast("Failed to download invoice", "error");
      console.error(error);
    }
  };

  const handleSendInvoice = () => {
    setShowSendModal(true);
    onClose();
  };

  const handleRecordPayment = () => {
    setShowRecordPayment(true);
    onClose();
  };

  const handleGeneratePaymentLink = () => {
    setShowPaymentPrompt(true); // Show the payment link modal
    onClose(); // Close the first timer modal
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl overflow-auto max-h-[90vh]">
        <div className="bg-blue-50 p-4 sticky top-0">
          <h2 className="text-xl font-semibold text-gray-800">
            Everything You Need to Get Paid — Faster!
          </h2>
        </div>

        <div className="p-6">
          <h3 className="text-2xl font-bold mb-4">Did you know?</h3>
          <p className="text-gray-700 mb-6">
            There's so much more you can do with Esemie Invoicing!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Download Invoice */}
            <div
              className={`border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors ${isLoading && "bg-blue-50"}`}
              onClick={handleDownloadInvoice}
            >
              <div className="flex items-start">
                <div className="mr-3 text-blue-600">
                  <DownloadIcon />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">
                    {isLoading ? "Downloading..." : "Download Invoice"}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Save a copy for your records or share it manually.
                  </p>
                </div>
              </div>
            </div>

            {/* Send Invoice */}
            <div
              className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={handleSendInvoice}
            >
              <div className="flex items-start">
                <div className="mr-3 text-blue-600">
                  <SendIcon />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">
                    Send Invoice to Customer
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Share via SMS, WhatsApp, or email — straight from Esemie.
                  </p>
                </div>
              </div>
            </div>

            {/* Record Payment */}
            {invoice?.status !== Statuses.PAID.toLowerCase() && (
              <div
                className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={handleRecordPayment}
              >
                <div className="flex items-start">
                  <div className="mr-3 text-blue-600">
                    <RecordIcon />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">
                      Record Payment
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Got paid outside the app? Log it in and keep things up to
                      date.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* payment link */}
            <div
              className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={handleGeneratePaymentLink}
            >
              <div className="flex items-start">
                <div className="mr-3 text-blue-600">
                  <ClipIcon />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">
                    Copy Payment Link
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Make it super easy for your customer to pay online.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstTimerModal;
